import { useEffect, useRef, useState } from 'react';
import { useLanguage, Localizer, getTextLocalizer } from '../../locale/useTextLocalizer';
import {
  InputChangeEvent,
  ValidateRangeInputProps,
  NumberFormatOptions,
  ValidateNumericInputType,
} from './types';
import { parseAndFormatInput, validateNumericInput } from './useInputValidationAdapter';
import { BaseInputProps } from './__internal__/BaseInput';
import bundle from './NovaComponents-Input-gui-icu.properties';
import { addBundle } from '../../locale/createUseTextLocalizer';

import { OmitStrict } from '../../utility-types';
import { getNumberSeparators } from './useInputValidationAdapter';
import { useEarcon, EarconName } from '../../hooks/useEarcon';
import { isNullish } from '@sas/nova-core/typeguards';
addBundle(bundle);

let localize: Localizer;
getTextLocalizer('NovaComponents-Input-gui-icu').then((localizer) => {
  localize = localizer;
});

/**
 * This is a range validator for setting min and max values.
 * @param props  of type ValidateRangeInputProps
 * @returns values{rawValue, formatValue}, isError, status, message
 *
 */
export const validateRangeInput = (props: ValidateRangeInputProps): ValidateNumericInputType => {
  let isError = false,
    status: BaseInputProps['status'] = undefined,
    message: BaseInputProps['message'] = undefined;

  const incMode = props.inclusiveMode! >= 0 && props.inclusiveMode! <= 3 ? props.inclusiveMode : 3;

  // to validate numeric value
  const result = validateNumericInput(
    props.value,
    props.appLocale,
    props.isRequired,
    props.allowIntegersOnly,
    props.isLive,
    props.minLength,
    props.numberFormatOptions
  );

  if (result.isError) {
    isError = result.isError;
    status = result.status;
    message = result.message;
  } else {
    if (props.value) {
      const parsedValue = result.values.rawValue;
      const maxValueLocaleString = props.maxValue?.toLocaleString(
        props.appLocale,
        props.numberFormatOptions
      );
      const minValueLocaleString = props.minValue?.toLocaleString(
        props.appLocale,
        props.numberFormatOptions
      );
      if (parsedValue > props.maxValue!) {
        if (incMode! < 2) {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.maxRangeExclusive.msg', {
            maxValue: maxValueLocaleString!,
          });
        } else {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.maxRangeInclusive.msg', {
            maxValue: maxValueLocaleString!,
          });
        }
      } else if (parsedValue === props.maxValue! && incMode! < 2) {
        isError = true;
        status = 'alert';
        message = localize('rangeValidator.maxRangeExclusive.msg', {
          maxValue: maxValueLocaleString!,
        });
      } else if (parsedValue < props.minValue!) {
        if (incMode === 0 || incMode === 2) {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.minRangeExclusive.msg', {
            minValue: minValueLocaleString!,
          });
        } else {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.minRangeInclusive.msg', {
            minValue: minValueLocaleString!,
          });
        }
      } else if (parsedValue === props.minValue! && !(incMode === 1 || incMode === 3)) {
        isError = true;
        status = 'alert';
        message = localize('rangeValidator.minRangeExclusive.msg', {
          minValue: minValueLocaleString!,
        });
      }
    }
  }

  return { isError, status, message, values: result.values };
};

export interface RangeValidation extends OmitStrict<ValidateRangeInputProps, 'value'> {
  validateInputFunction: (
    value: string,
    appLocale: string,
    isRequired?: boolean,
    allowIntegersOnly?: boolean,
    isLive?: boolean,
    minLength?: number,
    /**
     * These options are passed through directly to Intl.NumberFormat as number formatting options.
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat#using_options
     */
    numberFormatOptions?: NumberFormatOptions
  ) => ValidateNumericInputType;
  /**
   * initialValue is always a string just as the value property is a string.  If the initiaValue string represents
   * a number, the number should be unformatted without thousands separators, such that proper locale-specific
   * formatting can occur in the validator. For example, use initialValue: '10000' not initialValue: '10,000'.
   */
  initialValue?: string;
  disableAutoFormatting?: boolean;
}
/**
 * This hook provide functionality to handle events such as onChange,onBlur
 * & onCancel with empty initial value.
 * For those who doesn't wish to use this,
 * @function validateRangeInput is available for consumption
 * @param props
 *
 */
export const useRangeValidationAdapter = (props: RangeValidation) => {
  const earcon = useEarcon({ source: EarconName.InputFull });
  const appLocale = props.appLocale ?? useLanguage();
  const initialValue = props.initialValue
    ? parseFloat(props.initialValue).toLocaleString(appLocale)
    : '';
  const [value, setValue] = useState(initialValue ?? '');
  const [rawValue, setRawValue] = useState(initialValue ? Number(initialValue) : undefined);
  const [currentLocale, setCurrentLocale] = useState('');

  const [errors, setErrors] = useState<{
    message: BaseInputProps['message'];
    status: BaseInputProps['status'];
  }>({ status: undefined, message: '' });

  const handleChange = (event: InputChangeEvent) => {
    const liveValue = event.value;

    const [decimalSeparator] = getNumberSeparators(appLocale);
    const decimalValue =
      liveValue.lastIndexOf(decimalSeparator) !== -1
        ? liveValue.slice(liveValue.lastIndexOf(decimalSeparator) + 1)
        : '';

    const result = props.validateInputFunction(
      liveValue,
      appLocale,
      props.isRequired,
      props.allowIntegersOnly,
      true,
      props.minLength,
      props.numberFormatOptions
    );

    setRawValue(result.values.rawValue);
    result.isError
      ? setErrors({
          status: result.status,
          message: result.message,
        })
      : setErrors({ status: undefined, message: undefined });
    if (
      decimalValue !== '' &&
      props.numberFormatOptions?.maximumFractionDigits &&
      decimalValue.length > props.numberFormatOptions?.maximumFractionDigits
    ) {
      earcon.play();
    } else {
      setValue(liveValue);
    }
  };
  const handleBlur = () => {
    return handleBlurAndFocus(false);
  };

  const handleBlurAndFocus = (focused: boolean) => {
    if (isNullish(props.minValue) && isNullish(props.maxValue)) {
      return;
    }
    const result = validateRangeInput({
      value,
      minValue: props.minValue,
      maxValue: props.maxValue,
      inclusiveMode: props.inclusiveMode,
      appLocale,
      isRequired: props.isRequired,
      allowIntegersOnly: props.allowIntegersOnly,
      isLive: false,
      minLength: props.minLength,
      numberFormatOptions: props.numberFormatOptions,
    });

    setCurrentLocale(appLocale);
    setRawValue(undefined);

    if (result.isError) {
      setValue(value);
    } else if (value === '') {
      setValue('');
    } else if (!focused && !props.disableAutoFormatting) {
      setValue(result.values.formatValue);
    } else if (focused && !props.disableAutoFormatting) {
      const separators = getNumberSeparators(appLocale);
      const formatValueWithoutGroupingSeparator = result.values.formatValue.replace(
        new RegExp(`[${separators[1]}]`, 'gu'),
        ''
      );
      setValue(formatValueWithoutGroupingSeparator);
    } else {
      setValue(value);
      setRawValue(result.values.rawValue);
    }
    result.isError
      ? setErrors({
          status: result.status,
          message: result.message,
        })
      : setErrors({ status: undefined, message: undefined });
  };

  const handleFocus = () => {
    return handleBlurAndFocus(true);
  };
  const handleCommit = handleBlur;
  const handleCancel = () => {
    setValue('');
    setRawValue(undefined);
    setErrors({ status: undefined, message: undefined });
  };

  const revalidateRef = useRef<() => void>();
  const isInitializedRef = useRef(false);
  revalidateRef.current = handleBlur;

  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }
    revalidateRef.current?.();
  }, [
    props.minValue,
    props.maxValue,
    props.inclusiveMode,
    props.isRequired,
    props.allowIntegersOnly,
    props.minLength,
    props.numberFormatOptions?.maximumFractionDigits,
    props.numberFormatOptions?.minimumFractionDigits,
  ]);

  useEffect(() => {
    if (!initialValue || errors.status || appLocale === currentLocale) return;

    setCurrentLocale(appLocale);

    const result = parseAndFormatInput(
      rawValue ? rawValue.toString() : initialValue,
      appLocale,
      props.numberFormatOptions
    );
    if (result && !isNaN(parseFloat(result.formatValue))) {
      setValue(result.formatValue);
      setRawValue(result.rawValue);
    }
  }, [appLocale, currentLocale, rawValue, errors.status, initialValue, props.numberFormatOptions]);

  return {
    handleChange,
    value,
    errors,
    handleBlur,
    handleCancel,
    handleCommit,
    handleFocus,
  };
};
